import { createContext } from 'react';
import type { ReactNode } from 'react';
import { useThemeColorPalette } from '@onramp/utils/useThemeColorPalette';
import { domMax } from 'framer-motion';
import { styled } from 'linaria/react';
import type { Spectrum } from '@cbhq/cds-common';
import { usePalette } from '@cbhq/cds-web/hooks/usePalette';
import { FeatureFlagProvider, ThemeProvider } from '@cbhq/cds-web/system';
export type WidgetBackgroundVariant = 'solid' | 'overlay' | 'transparent';
type AppThemeProviderProps = {
  spectrum: Spectrum;
  children: ReactNode;
  /* Changes whether to show a solid background or modal overlay */
  widgetBackgroundVariant?: WidgetBackgroundVariant;
};
export const AppThemeProvider = ({
  children,
  spectrum,
  widgetBackgroundVariant = 'solid'
}: AppThemeProviderProps) => {
  const palette = useThemeColorPalette();
  return <FeatureFlagProvider>
      <ThemeProvider spectrum={spectrum} palette={palette} motionFeatures={domMax}>
        <WidgetBackgroundVariantContext.Provider value={widgetBackgroundVariant}>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          <style>{`:root{color-scheme: only ${spectrum};}`}</style>
          <Background widgetBackgroundVariant={widgetBackgroundVariant}>{children}</Background>
        </WidgetBackgroundVariantContext.Provider>
      </ThemeProvider>
    </FeatureFlagProvider>;
};
const Background = ({
  children,
  widgetBackgroundVariant
}: {
  children: ReactNode;
} & Pick<AppThemeProviderProps, 'widgetBackgroundVariant'>) => {
  const {
    background
  } = usePalette();
  const backgroundColor = (() => {
    switch (widgetBackgroundVariant) {
      case 'solid':
        return background;
      case 'overlay':
        return 'rgba(50, 53, 61, 0.33)';
      case 'transparent':
        return 'transparent';
      default:
        return background;
    }
  })();
  return <Wrapper $backgroundColor={backgroundColor}>{children}</Wrapper>;
};
export const WidgetBackgroundVariantContext = createContext<WidgetBackgroundVariant>('solid');
const Wrapper = /*#__PURE__*/styled("div")({
  name: "Wrapper",
  class: "w1sjvtkb",
  vars: {
    "w1sjvtkb-0": [props => props.$backgroundColor]
  }
});

require("./AppThemeProvider.linaria.module.css!=!../../node_modules/linaria/lib/outputCssLoader.js?cacheProvider=!./AppThemeProvider.tsx");